import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * App-wide music player state + player elements. Lives ONCE at the
 * App root so route changes don't unmount anything and playback
 * survives every route transition.
 *
 * Handles TWO track kinds:
 *   • kind: "file"     — plays via a native <audio> element.
 *   • kind: "youtube"  — plays via a hidden YouTube IFrame Player
 *                        (loaded on demand from
 *                        https://www.youtube.com/iframe_api).
 *
 * The consumer (`MusicButton`) doesn't care which kind is current —
 * `toggleMute` and `setVolume` route to whichever engine is active.
 * Advancing on end (`onEnded` for audio, IFrame state=0 for YT) works
 * for both.
 */
const LS_KEY = "ch_music_v2";
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const YT_HOST_ID = "music-yt-frame";

const MusicPlayerCtx = createContext(null);

function readPrefs() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || "{}"); }
  catch (_) { return {}; }
}
function writePrefs(p) {
  try { localStorage.setItem(LS_KEY, JSON.stringify(p)); }
  catch (_) { /* private mode */ }
}

// Load the YouTube IFrame API script exactly once for the whole page.
let ytLoadingPromise = null;
function loadYouTubeAPI() {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytLoadingPromise) return ytLoadingPromise;
  ytLoadingPromise = new Promise((resolve) => {
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof prev === "function") { try { prev(); } catch (_) { /* ignore */ } }
      resolve(window.YT);
    };
    const s = document.createElement("script");
    s.src = "https://www.youtube.com/iframe_api";
    s.async = true;
    document.head.appendChild(s);
  });
  return ytLoadingPromise;
}

export function MusicPlayerProvider({ children }) {
  const { user, church } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [idx, setIdx] = useState(0);
  const [muted, setMuted] = useState(() => readPrefs().muted !== false);
  const [volume, setVolume] = useState(() => {
    const v = readPrefs().volume;
    return typeof v === "number" && v >= 0 && v <= 1 ? v : 0.6;
  });
  const audioRef = useRef(null);
  const ytPlayerRef = useRef(null);
  // Web Audio graph — created lazily on the first user gesture so we can
  // control gain (volume) on iOS Safari, which ignores the standard
  // HTMLMediaElement.volume property. `audioCtxRef` / `gainRef` /
  // `sourceRef` all null until first Play or Mute tap.
  const audioCtxRef = useRef(null);
  const gainRef = useRef(null);
  const sourceRef = useRef(null);

  const ensureWebAudio = useCallback(() => {
    if (audioCtxRef.current) return;
    const el = audioRef.current;
    if (!el) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      const ctx = new AC();
      // iOS starts every AudioContext in "suspended" state — needs a
      // resume() call from within a user gesture (which is exactly
      // what toggleMute / setVolume both are).
      ctx.resume?.().catch(() => { /* not fatal */ });
      const src = ctx.createMediaElementSource(el);
      const gain = ctx.createGain();
      gain.gain.value = muted ? 0 : volume;
      src.connect(gain).connect(ctx.destination);
      audioCtxRef.current = ctx;
      gainRef.current = gain;
      sourceRef.current = src;
    } catch (_) {
      /* Web Audio init failed (rare — usually cross-origin media). We
         fall back to el.volume, which works on desktop/Android but not
         iOS. Better than crashing. */
    }
  }, [muted, volume]);

  const musicKilled = useMemo(
    () => Array.isArray(church?._platform_disabled) && church._platform_disabled.includes("music"),
    [church?._platform_disabled],
  );

  const loadPlaylist = useCallback(async () => {
    if (musicKilled) { setTracks([]); return; }
    // Fisher–Yates shuffle so every app open / music-tab refresh
    // surfaces a different song first. Kept inline to avoid a stray
    // top-level util — this is the only place we shuffle.
    const shuffle = (arr) => {
      const a = Array.isArray(arr) ? arr.slice() : [];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    };
    try {
      if (user) {
        const r = await api.get("/audio/playlist");
        setTracks(shuffle(r.data));
        setIdx(0);
        return;
      }
      let code = church?.church_code || "";
      if (!code) {
        try {
          const dc = await fetch(`${BACKEND}/api/public/default-church`);
          if (dc.ok) code = (await dc.json())?.church_code || "";
        } catch (_) { /* offline */ }
      }
      if (!code) { setTracks([]); return; }
      const r = await fetch(`${BACKEND}/api/public/church/${code}/audio`);
      if (r.ok) {
        setTracks(shuffle(await r.json()));
        setIdx(0);
      }
    } catch (_) { setTracks([]); }
  }, [user, church?.church_code, musicKilled]);
  useEffect(() => { loadPlaylist(); }, [loadPlaylist]);

  useEffect(() => { writePrefs({ muted, volume }); }, [muted, volume]);

  const current = tracks[idx];
  const currentKind =
    current?.kind === "youtube" ? "youtube"
    : current?.kind === "iframe" ? "iframe"
    : "file";

  // ---- Native <audio> src for file tracks ----
  const currentSrc = useMemo(() => {
    if (!current || currentKind !== "file") return null;
    return current.url?.startsWith("http") ? current.url : `${BACKEND}${current.url}`;
  }, [current, currentKind]);

  useEffect(() => {
    const el = audioRef.current;
    if (el) {
      el.volume = volume;
      el.muted = muted;
    }
    // The GainNode is what actually controls volume on iOS Safari,
    // since iOS ignores HTMLMediaElement.volume. On other platforms,
    // both routes update in lockstep so behavior stays identical.
    if (gainRef.current) {
      try { gainRef.current.gain.value = muted ? 0 : volume; }
      catch (_) { /* ignore */ }
    }
  }, [volume, muted]);

  const onEnded = useCallback(() => {
    if (tracks.length === 0) return;
    setIdx((i) => (i + 1) % tracks.length);
  }, [tracks.length]);

  useEffect(() => {
    if (currentKind !== "file") { audioRef.current?.pause?.(); return; }
    const el = audioRef.current;
    if (!el || !currentSrc) return;
    if (!muted) el.play().catch(() => { /* needs user gesture */ });
  }, [currentSrc, muted, currentKind]);

  // ---- YouTube IFrame Player ----
  useEffect(() => {
    if (currentKind !== "youtube" || !current?.youtube_id) {
      // Stop any existing YT player so it doesn't linger behind a
      // file track.
      try { ytPlayerRef.current?.stopVideo?.(); } catch (_) { /* ignore */ }
      return;
    }
    let cancelled = false;
    (async () => {
      const YT = await loadYouTubeAPI();
      if (cancelled || !YT) return;
      // Ensure the host div exists (rendered below).
      const host = document.getElementById(YT_HOST_ID);
      if (!host) return;
      if (!ytPlayerRef.current) {
        ytPlayerRef.current = new YT.Player(YT_HOST_ID, {
          height: "1",
          width: "1",
          videoId: current.youtube_id,
          playerVars: {
            autoplay: muted ? 0 : 1,
            controls: 0,
            disablekb: 1,
            modestbranding: 1,
            playsinline: 1,
            rel: 0,
          },
          events: {
            onReady: (e) => {
              try {
                e.target.setVolume(Math.round(volume * 100));
                if (muted) e.target.mute(); else { e.target.unMute(); e.target.playVideo(); }
              } catch (_) { /* ignore */ }
            },
            onStateChange: (e) => {
              // 0 === ENDED — advance to the next track.
              if (e && e.data === 0) onEnded();
            },
            onError: () => onEnded(),
          },
        });
      } else {
        try {
          ytPlayerRef.current.loadVideoById(current.youtube_id);
          ytPlayerRef.current.setVolume(Math.round(volume * 100));
          if (muted) ytPlayerRef.current.mute();
          else { ytPlayerRef.current.unMute(); ytPlayerRef.current.playVideo(); }
        } catch (_) { /* ignore */ }
      }
    })();
    return () => { cancelled = true; };
  }, [current?.youtube_id, currentKind, muted, volume, onEnded]);

  // Sync volume/mute changes to the YT player between track swaps.
  useEffect(() => {
    if (!ytPlayerRef.current || currentKind !== "youtube") return;
    try {
      ytPlayerRef.current.setVolume(Math.round(volume * 100));
      if (muted) ytPlayerRef.current.mute();
      else { ytPlayerRef.current.unMute(); ytPlayerRef.current.playVideo(); }
    } catch (_) { /* ignore */ }
  }, [muted, volume, currentKind]);

  // ---- Third engine: generic iframe (Restream, Vimeo, SoundCloud…) ----
  // We can't set volume or mute programmatically for arbitrary
  // providers, so "mute" here means "unload the iframe src" (which
  // stops the stream entirely). Unmuting restores the src. There is
  // no reliable "ended" event either — iframe tracks play forever
  // until the admin advances the playlist or mutes.
  const iframeSrc = useMemo(() => {
    if (currentKind !== "iframe" || !current?.url) return null;
    if (muted) return null;
    return current.url;
  }, [currentKind, current?.url, muted]);

  const toggleMute = useCallback(() => {
    ensureWebAudio();
    setMuted((m) => !m);
  }, [ensureWebAudio]);
  const changeVolume = useCallback((v) => {
    ensureWebAudio();
    setVolume(v);
    if (v > 0) setMuted(false);
  }, [ensureWebAudio]);

  const value = useMemo(() => ({
    tracks,
    muted,
    volume,
    musicKilled,
    toggleMute,
    setVolume: changeVolume,
    hasTracks: tracks.length > 0,
  }), [tracks, muted, volume, musicKilled, toggleMute, changeVolume]);

  return (
    <MusicPlayerCtx.Provider value={value}>
      {/* Both engines live in the tree once, at the App root. Route
          changes never unmount them so playback survives. */}
      <audio
        ref={audioRef}
        src={currentSrc || undefined}
        onEnded={onEnded}
        onError={onEnded}
        preload="none"
      />
      <div
        id={YT_HOST_ID}
        style={{
          position: "fixed",
          bottom: "-9999px",
          left: "-9999px",
          width: 1,
          height: 1,
          pointerEvents: "none",
          opacity: 0,
        }}
        aria-hidden="true"
      />
      {/* Third engine: generic embed iframe (Restream, Vimeo, etc.).
          Hidden off-screen so it just plays audio in the background;
          when muted we unload the src entirely to stop the stream. */}
      {iframeSrc && (
        <iframe
          key={iframeSrc}
          title="Background music (embed)"
          src={iframeSrc}
          allow="autoplay; encrypted-media"
          style={{
            position: "fixed",
            bottom: "-9999px",
            left: "-9999px",
            width: 1,
            height: 1,
            border: 0,
            pointerEvents: "none",
            opacity: 0,
          }}
          aria-hidden="true"
        />
      )}
      {children}
    </MusicPlayerCtx.Provider>
  );
}

export function useMusicPlayer() {
  const ctx = useContext(MusicPlayerCtx);
  if (!ctx) {
    return {
      tracks: [],
      muted: true,
      volume: 0.6,
      musicKilled: true,
      toggleMute: () => {},
      setVolume: () => {},
      hasTracks: false,
    };
  }
  return ctx;
}
